import React from 'react';
import PropTypes from 'prop-types';
import BreakpointObserver, { sizes } from '@decisiv/breakpoint-observer';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import Button from '@decisiv/ui-components/lib/components/Button';
import { Badge, H2 } from '@decisiv/ui-components';
import Lock from '@decisiv/iconix/lib/components/Lock';

import usePasswordReset from './hooks/usePasswordReset';
import {
  ButtonsContainer,
  PasswordResetContainer,
} from './components/styledComponents';

function PasswordReset({ csrfToken, backLoginClick }) {
  // The breakpoints object defines the minimum widths for specific breakpoint names.
  const breakpoints = {
    [sizes.SM]: 300,
    [sizes.MD]: 600,
  };

  const {
    identity,
    setIdentity,
    requestResetPassword,
    requestingResetPassword,
  } = usePasswordReset(csrfToken);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (identity.trim() && !requestingResetPassword) {
        requestResetPassword();
        setIdentity('');
      }
    }
  };

  return (
    <BreakpointObserver breakpoints={breakpoints}>
      {({
        breakpoint, // The currently active breakpoint. Defaults to `sizes.XS`.
        dimensions, // An object containing the `height` & `width` of the observed DOM element.
        observedElementRef, // The ref must be assigned to the DOM element you want to observe.
      }) => (
        <div ref={observedElementRef}>
          <PasswordResetContainer>
            <Badge
              aria-label="lock badge"
              color="information"
              icon={Lock}
              size="large"
              palette="bright"
            />
            <H2 style={{ marginTop: '10px' }}>Forgot Password?</H2>
            <P color="alaskanHusky" style={{ textAlign: 'center' }}>
              Enter your account&apos;s username, or email address, and
              we&apos;ll send you a link to reset your password.
            </P>
            <ButtonsContainer>
              <Flex
                flexDirection="row"
                marginBottom={1}
                style={{ width: '100%' }}
              >
                <Flex flex={1}>
                  <TextField
                    required
                    style={{ width: '100%' }}
                    label="Username or Email"
                    name="identity"
                    value={identity}
                    onChange={(e) => {
                      setIdentity(e.target.value);
                    }}
                    onKeyPress={handleKeyPress}
                  />
                </Flex>
              </Flex>

              <Button
                text="Send Link"
                onClick={() => {
                  requestResetPassword();
                  setIdentity('');
                }}
                disabled={!identity.trim() || requestingResetPassword}
              />

              <Button
                text="Return to Login"
                variant="ghost"
                size="small"
                onClick={backLoginClick}
                style={{ marginTop: '25px' }}
              />
            </ButtonsContainer>
          </PasswordResetContainer>
        </div>
      )}
    </BreakpointObserver>
  );
}

PasswordReset.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  backLoginClick: PropTypes.func,
};

PasswordReset.defaultProps = {
  backLoginClick: () => {},
};

export default PasswordReset;
